import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import isNil from 'lodash/isNil';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import RadioGroup from '@mui/material/RadioGroup';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  useMixpanelTrack,
  useNotify,
  useUserSettings,
} from 'core/hooks';
import Radio from 'pages/Dashboard/components/Input/Radio';
import {
  FilterContext,
} from 'core/Filters/context';
import {
  useUpsertUserSettings,
} from 'pages/Dashboard/hooks/useUserSettings';
import Button from 'pages/Dashboard/components/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import defer from 'lodash/defer';
import FiltersIcon from 'core/icons/filters';
import {
  isFilterEmpty,
} from 'core/Filters/util';
import {
  FilterBadge,
} from 'core/Filters/FilterWrapper';

export type CalendarViewSettings = {
  viewSize: 'large' | 'medium' | 'small';
  showWeekend: boolean;
  showExtendedHours: boolean;
  includeCancelledAndNoShow: boolean;
};

type Props = {
  filtersRef: RefObject<HTMLDivElement>;
  value?: CalendarViewSettings;
  onChange?: (value: CalendarViewSettings) => void;
  onlyFilters?: boolean;
  label?: string;
  pageObject?: 'appointment' | 'encounter';
  isLoading?: boolean;
};

const viewSizes = Object.freeze([
  { value: 'large', label: 'Large' },
  { value: 'medium', label: 'Normal' },
  { value: 'small', label: 'Compact' },
]);

export const defaultCalendarViewSettings: CalendarViewSettings = {
  viewSize: 'small',
  showWeekend: false,
  showExtendedHours: false,
  includeCancelledAndNoShow: false,
};

type SettingsViewProps = Props & {
  onClose: () => void;
  setEditsCount: Dispatch<SetStateAction<number>>;
  isOpened: boolean;
};

function SettingsView({
  value,
  onChange,
  filtersRef,
  onlyFilters = false,
  label,
  pageObject,
  onClose,
  setEditsCount,
  isOpened,
}: SettingsViewProps) {
  const defaultValues = isEmpty(value) || isNil(value)
    ? defaultCalendarViewSettings
    : value;

  const {
    control,
    watch,
    reset,
    formState: { isDirty },
  } = useForm<CalendarViewSettings>({
    defaultValues: defaultValues as CalendarViewSettings,
  });

  const notify = useNotify();
  const {
    settings,
    setSettings,
  } = useUserSettings();
  const {
    mutateAsync: updateSettings,
    isPending: isLoading,
  } = useUpsertUserSettings();
  const {
    clearAll,
    filters,
  } = useContext(FilterContext);
  const data = watch();

  const applyFilter = async () => {
    const preferenceJson = JSON.stringify({
      ...settings,
      [`${pageObject}Filters`]: filters,
      ...(onlyFilters ? {} : { [`${pageObject}Settings`]: data }),
    });

    const response = await updateSettings({ preferenceJson });
    setSettings(JSON.parse(response?.preferenceJson ?? '{}'));

    notify({
      message: `${capitalize(pageObject)} Filters${onlyFilters ? ' ' : ' and Settings '}were saved.`,
      variant: 'success',
    });
    return true;
  };

  const handleSave = async () => {
    applyFilter?.();
    onClose();
  };

  useEffect(() => {
    if (isDirty && !onlyFilters) {
      reset(data, { keepDirty: false, keepTouched: false });
      setEditsCount((prev: number) => prev + 1);

      onChange?.(data);
    }
  }, [isDirty, data, onlyFilters]);

  return (
    <div className={clsx(
      'absolute top-[-6px] right-0 w-80 bg-white shadow-2xl shadow-gray-400 rounded-lg z-10 max-h-[80vh] flex flex-col gap-4 lg:right-auto lg:-left-px',
      { hidden: !isOpened },
    )}
    >
      <div className="flex justify-between items-center pl-3 pt-1.5">
        <h3 className="flex gap-2 items-center text-xs text-neutral-700 font-semibold">
          <FiltersIcon className="!w-4 !h-4 !text-neutral-700" />
          {label}
        </h3>
        <div className="flex gap-3 nw-clear-filters">
          <Button
            variant="text"
            color="primary"
            onClick={clearAll}
          >
            Clear
          </Button>
          <IconButton
            onClick={onClose}
          >
            <CloseIcon className="!w-4 !h-4 !text-gray-400" />
          </IconButton>
        </div>
      </div>
      <div className="flex-1 w-full overflow-y-auto">
        <div className="w-full flex flex-col gap-4 px-4">
          <div
            ref={filtersRef}
            className="flex flex-col gap-4"
          />
          <div className={clsx('flex flex-col w-full', {
            'gap-4 border-t border-t-gray-300': !onlyFilters,
          })}
          >
            {!onlyFilters && (
            <>
              <Controller
                control={control}
                name="viewSize"
                render={({ field }) => (
                  <Wrapper
                    id="calendar-view-size"
                    className="text-xs w-full"
                    labelClasses="text-neutral-700 text-xs pt-2"
                    label="View Settings"
                  >
                    <RadioGroup
                      row
                      className="mt-2 px-2 w-full flex flex-row justify-between"
                      {...field}
                    >
                      {viewSizes.map((size) => (
                        <Radio
                          key={size.value}
                          {...size}
                          className={clsx('!p-0', {
                            '!text-blue-500': size.value === field.value,
                          })}
                        />
                      ))}
                    </RadioGroup>
                  </Wrapper>
                )}
              />
              <Controller
                control={control}
                name="showWeekend"
                render={({ field: { value: checked, ...field } }) => (
                  <Checkbox
                    {...field}
                    checked={Boolean(checked)}
                    id="toggle-weekend"
                    className={checked ? '!text-blue-500' : ''}
                    label="Include Weekend Days"
                  />
                )}
              />
              <Controller
                control={control}
                name="showExtendedHours"
                render={({ field: { value: checked, ...field } }) => (
                  <Checkbox
                    {...field}
                    checked={Boolean(checked)}
                    id="toggle-extended-hours"
                    className={checked ? '!text-blue-500' : ''}
                    label="Show Extended Hours"
                  />
                )}
              />
              <Controller
                control={control}
                name="includeCancelledAndNoShow"
                render={({ field: { value: checked, ...field } }) => (
                  <Checkbox
                    {...field}
                    checked={Boolean(checked)}
                    id="toggle-include-cancelled-and-no-show"
                    className={checked ? '!text-blue-500' : ''}
                    label={{
                      text: (
                        <>
                          {'Include '}
                          <strong>Cancelled & No Show</strong>
                          {' Appointments'}
                        </>
                      ),
                      placement: 'top',
                    }}
                    containerClassName="!items-start"
                  />
                )}
              />
            </>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3 p-4">
        <Button
          variant="outlined"
          onClick={onClose}
          className="nw-close-settings"
        >
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="nw-save-settings"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export default function SettingsButton({
  value,
  label = 'Filters and Settings',
  pageObject = 'appointment',
  isLoading = false,
  ...props
}: Props) {
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const handleClick = () => {
    setIsOpened(!isOpened);
  };
  const [editsCount, setEditsCount] = useState<number>(0);
  const mixpanelTrack = useMixpanelTrack();
  const {
    filters,
  } = useContext(FilterContext);

  const applied = !isFilterEmpty(filters);
  const onClose = () => {
    setIsOpened(false);
    if (editsCount > 0) {
      mixpanelTrack(`changed${capitalize(pageObject)}Settings`, {
        ...value,
        editsCount,
      });
    }
    setEditsCount(0);
  };

  const handleClickAway = () => {
    defer(() => {
      const isPartOfSettings = !isNil(document.activeElement?.closest('.MuiPopover-root, .settings-button-select'));
      if (!isPartOfSettings && isOpened) {
        onClose();
      }
    });
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className="relative">
        <Button
          variant="outlined"
          className="nw-settings-filters !pl-2.5 flex gap-2"
          onClick={handleClick}
        >
          <FilterBadge showBadge={applied} />
          <span className="hidden lg:block">{label}</span>
        </Button>
        {!isLoading && (
          <SettingsView
            {...props}
            value={value}
            label={label}
            pageObject={pageObject}
            setEditsCount={setEditsCount}
            onClose={onClose}
            isOpened={isOpened}
          />
        )}
      </div>
    </ClickAwayListener>
  );
}
